import {
  Alert,
  Button,
  Card,
  Form,
  FormGroup,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
} from "reactstrap";
import { FormEvent, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Toggle } from "rsuite";

import gcWordmark from "../../assets/img/growthcloud/growthcloud_wordmark.svg";
import LinkedInLogo from "../../assets/img/icons/common/linkedin.svg";
import OfficeLogo from "../../assets/img/icons/common/office365.svg";
import AmazonLogo from "../../assets/img/icons/common/amazon.svg";
import GoogleLogo from "../../assets/img/icons/common/google.svg";
import AppHelmet from "../../components/AppComponents/AppHelmet";
import AuthFooter from "../../components/Footers/AuthFooter";
import PendingApprovalModal from "./PendingApprovalModal";
import useLogin from "../../api/api-hooks/auth/useLogin";
import AppSpinner from "../../elements/AppSpinner";
import OAuthButton from "./OAuthButton";
import AuthAPI from "../../api/api/AuthAPI";

export const methods = {
  amazon: "Amazon",
  google: "Google",
  office365: "Office 365",
  linkedin: "LinkedIn",
} as const;

export type Method = keyof typeof methods;

export const errorLoginMessages = {
  pendingApproval:
    "Your account is pending approval. Please contact your administrator for more information.",
};

const LoginPage = () => {
  return (
    <div
      style={{
        backgroundImage: `url(/img/login-bg.png)`,
      }}
      className="tw:flex tw:justify-center tw:items-center tw:bg-no-repeat tw:bg-cover tw:bg-fixed"
    >
      <AppHelmet title="Sign In or Get Started with GrowthCloud.com" />
      <div className="tw:flex tw:justify-center tw:w-full tw:overflow-auto">
        <div className="tw:md:flex tw:justify-center tw:items-center tw:h-screen tw:w-0 tw:min-[992px]:w-1/2">
          <p className="tw:text-white tw:text-6xl tw:font-bold tw:leading-relaxed tw:hidden tw:min-[992px]:block tw:max-w-[575px] tw:pl-8">
            AI-Powered Growth Solutions For Business
          </p>
        </div>
        <div className="tw:flex tw:justify-center tw:self-center tw:md:py-5 tw:md:pr-5 tw:w-full tw:min-[992px]:w-1/2 tw:h-full">
          <LoginCard />
        </div>
      </div>
    </div>
  );
};

const LoginCard = () => {
  const navigate = useNavigate();
  const [redirecting, setRedirecting] = useState<Method | false>(false);
  const [form, setForm] = useState({ email: "", password: "" });
  const { login, loginLoading, loginError } = useLogin();
  const [inputType, setInputType] = useState<"password" | "text">("password");
  const [isPendingModalOpen, setPendingModalOpen] = useState(false);

  const handleModalClose = () => {
    setPendingModalOpen(false);
    navigate("/");
  };

  return (
    <>
      <Card className="tw:max-[576px]:min-w-screen tw:max-[576px]:min-h-dvh tw:p-7 tw:pb-0 tw:flex tw:justify-between tw:m-0 tw:w-full tw:max-w-[500px]">
        <div>
          <a className="tw:pb-4" href="https://growthcloud.com">
            <img alt="..." src={gcWordmark} className="tw:w-1/2" />
          </a>
          <h2 className="tw:pt-5 tw:pb-2">Login to your account</h2>
          <p className="tw:pb-2 tw:font-bold tw:text-xs">
            New to GrowthCloud?{" "}
            <Link className="tw:text-primary tw:font-medium" to="/register">
              Create Account
            </Link>
          </p>
          <div className="tw:justify-center tw:my-4 tw:px-3">
            <div className="tw:flex tw:justify-center tw:items-center tw:py-4">
              <OAuthButton
                onClick={() => redirectOAuth("office365")}
                logo={OfficeLogo}
                redirecting={redirecting}
                method="office365"
              />
              <OAuthButton
                onClick={() => redirectOAuth("google")}
                logo={GoogleLogo}
                redirecting={redirecting}
                method="google"
              />
              <OAuthButton
                onClick={() => redirectOAuth("amazon")}
                logo={AmazonLogo}
                redirecting={redirecting}
                method="amazon"
              />
              <OAuthButton
                onClick={() => redirectOAuth("linkedin")}
                logo={LinkedInLogo}
                redirecting={redirecting}
                method="linkedin"
              />
            </div>
            <div className="tw:py-4">
              <h5 className="tw:w-full tw:text-center tw:px-4 tw:border-b tw:border-solid tw:border-black tw:leading-[0.1rem]">
                <span className="tw:bg-white tw:px-3">or</span>
              </h5>
            </div>

            <div>
              <Form role="form" onSubmit={loginLocal}>
                <FormGroup>
                  <h5 className="tw:text-dark">Email</h5>
                  <Input
                    placeholder="Email"
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleInputChange}
                    required={true}
                    data-test="email"
                  />
                </FormGroup>
                <FormGroup>
                  <h5 className="tw:text-dark">Password</h5>
                  <InputGroup className="tw:mt-3">
                    <Input
                      placeholder="Password"
                      type={inputType}
                      autoComplete="off"
                      name="password"
                      value={form.password}
                      onChange={handleInputChange}
                      required={true}
                      data-test="password"
                      className="tw:border-r-0"
                    />
                    <InputGroupAddon addonType="append">
                      <InputGroupText
                        className="tw:cursor-pointer"
                        onClick={() => toggleShowPassword()}
                      >
                        <i
                          className={`mdi tw:text-lg tw:leading-none ${
                            inputType ? "mdi-eye-off" : "mdi-eye"
                          }`}
                        />
                      </InputGroupText>
                    </InputGroupAddon>
                  </InputGroup>
                  <div className="tw:mt-4 tw:flex tw:justify-between tw:text-[12px] tw:w-full">
                    <div className="tw:hidden tw:items-center tw:gap-2">
                      <Toggle className="tw:m-0" />
                      <p className="tw:font-bold tw:text-[12px] tw:m-0">
                        Remember me
                      </p>
                    </div>
                    <Link
                      className="tw:text-dark tw:font-bold"
                      to="/login/forgot"
                    >
                      Forgot password?
                    </Link>
                  </div>
                </FormGroup>

                <div style={{ width: "100%" }}>
                  <Button
                    disabled={loginLoading}
                    className="tw:px-4 tw:w-full"
                    color="primary"
                    type="submit"
                    data-test="signinButton"
                  >
                    {loginLoading ? (
                      <AppSpinner size="sm" color="white" />
                    ) : (
                      <>Sign In</>
                    )}
                  </Button>
                  <div className="tw:flex tw:justify-center tw:mt-6">
                    <a
                      className="tw:text-primary tw:text-[12px] tw:font-bold"
                      href="https://www.emerge360.com/growthcloud"
                      target="_blank"
                      rel="norefferer"
                    >
                      Learn More About GrowthCloud
                    </a>
                  </div>
                </div>
                {loginError && (
                  <Alert
                    color="warning"
                    className={`tw:mt-3 ${
                      loginError !== errorLoginMessages.pendingApproval
                        ? ""
                        : "tw:hidden"
                    }`}
                    data-test="signinError"
                  >
                    {loginError}
                  </Alert>
                )}
              </Form>
            </div>
          </div>
        </div>
        <AuthFooter />
      </Card>
      <PendingApprovalModal
        open={isPendingModalOpen}
        onClose={handleModalClose}
      />
    </>
  );

  async function redirectOAuth(provider: Method) {
    const clientID = await AuthAPI.getClientID().then(
      (d) => d.data.data.ClientID
    );

    setRedirecting(provider);
    const redirectTo = window.location.origin + "/oauth";
    const redirectURL = `${window.GC_API_URL}/auth/providers/${provider}?redirectTo=${redirectTo}&app-id=GrowthCloud&clientID=${clientID}`;

    const url = new URL(window.location.href);
    const r = url.searchParams.get("r");

    r && localStorage.setItem("loginRedirectURL", r);

    setTimeout(() => setRedirecting(false), 5000);
    window.location.href = redirectURL;
    return;
  }

  function loginLocal(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();

    login({
      email: form.email,
      password: form.password,
    });
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  function toggleShowPassword() {
    setInputType(inputType === "password" ? "text" : "password");
  }
};

export default LoginPage;
